import { faShareAlt, faTrash, faTrashUndo, faUserClock } from "@fortawesome/pro-solid-svg-icons"
import { faPaperPlane } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Space, Table, Tooltip, Typography } from "antd"
import type { ColumnsType } from "antd/es/table"
import dayjs from "dayjs"
import { useEffect, useMemo, useState } from "react"
import { Consult, CustomerDetail } from "./customerApi"
import { consultStatusLabel, sortConsultsByDate } from "./customerConsultsModel"
import { useCannedResponsesQuery, useConsultationProjectsQuery, useDownloadConsultationIcsMutation, useSalesLeadsQuery, useSalesUsersQuery } from "./customerQueries"
import ConsultationModal from "./ConsultationModal"
import SendConsultationInvitesModal from "./SendConsultationInvitesModal"

const { Text } = Typography

type Props = {
  customer: CustomerDetail
  consults: Consult[]
  formDirty: boolean
  onToggleRemoveConsult: (consult: Consult) => void
  onConsultSaved?: (saved: Consult) => void
  // Set by the page when + New → Consultation is chosen; the table opens a new
  // consultation and reports back so a later remount does not reopen it.
  newConsultRequested?: boolean
  onNewConsultOpened?: () => void
}

const CustomerConsultsTable = ({ customer, consults, formDirty, onToggleRemoveConsult, onConsultSaved, newConsultRequested, onNewConsultOpened }: Props) => {
  const [consultModalOpen, setConsultModalOpen] = useState(false)
  const [editingConsult, setEditingConsult] = useState<Consult | null>(null)
  const [sendModalOpen, setSendModalOpen] = useState(false)
  const [sendConsult, setSendConsult] = useState<Consult | null>(null)

  const salesUsersQuery = useSalesUsersQuery()
  const salesLeadsQuery = useSalesLeadsQuery()
  const cannedResponsesQuery = useCannedResponsesQuery()
  const projectsQuery = useConsultationProjectsQuery(customer.id, consultModalOpen)
  const downloadIcsMutation = useDownloadConsultationIcsMutation()

  const salesUserNames = useMemo(() => {
    const map: Record<string, string> = {}
    ;(salesUsersQuery.data ?? []).forEach(u => { map[u.email] = u.displayName })
    return map
  }, [salesUsersQuery.data])

  const openNewConsult = () => {
    setEditingConsult(null)
    setConsultModalOpen(true)
  }

  // Always acknowledge the request, even for an unsaved customer, so a stale
  // flag cannot open the form later when the customer id changes.
  useEffect(() => {
    if (!newConsultRequested) {
      return
    }
    if (customer.id !== 0) {
      openNewConsult()
    }
    onNewConsultOpened?.()
  }, [newConsultRequested, customer.id])

  const openEditConsult = (consult: Consult) => {
    setEditingConsult(consult)
    setConsultModalOpen(true)
  }

  const openSendInvites = (consult: Consult) => {
    setSendConsult(consult)
    setSendModalOpen(true)
  }

  const downloadIcs = (consult: Consult) => downloadIcsMutation.mutate(consult.id)

  const handleConsultSaved = (saved: Consult, send: boolean) => {
    onConsultSaved?.(saved)
    if (send) {
      openSendInvites(saved)
    }
  }

  const columns: ColumnsType<Consult> = [
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, consult) => {
        const canInvite = !!consult.consultationDate && !formDirty
        return <Space onClick={e => e.stopPropagation()}>
          {canInvite
            ? <>
                <Tooltip title="Send Invites">
                  <Button type="text" size="small" icon={<FontAwesomeIcon icon={faPaperPlane} />} onClick={() => openSendInvites(consult)} />
                </Tooltip>
                <Tooltip title="Download iCalendar">
                  <Button type="text" size="small" icon={<FontAwesomeIcon icon={faShareAlt} />} onClick={() => downloadIcs(consult)} />
                </Tooltip>
              </>
            : <>
                <Tooltip title="You must save your changes before sending invites">
                  <FontAwesomeIcon icon={faPaperPlane} style={{ opacity: 0.35 }} />
                </Tooltip>
                <Tooltip title="You must save your changes before creating iCal invites">
                  <FontAwesomeIcon icon={faShareAlt} style={{ opacity: 0.35 }} />
                </Tooltip>
              </>}
        </Space>
      },
    },
    { title: 'Date', key: 'date', render: (_, c) => (c.consultationDate ? dayjs(c.consultationDate).format('M/D/YYYY h:mm A') : '') },
    { title: 'Sales Person', key: 'salesPerson', render: (_, c) => salesUserNames[c.organizerEmail ?? ''] ?? '' },
    { title: 'Timeframe', dataIndex: 'timeFrame', key: 'timeFrame' },
    { title: 'Summary', dataIndex: 'summary', key: 'summary' },
    { title: 'Project', dataIndex: 'projectName', key: 'projectName' },
    { title: 'Status', key: 'status', render: (_, c) => consultStatusLabel(c.status) },
    { title: 'Updated', key: 'updated', align: 'right', render: (_, c) => (c.updated ? dayjs(c.updated).format('MMM D, YYYY') : '') },
    {
      title: '',
      key: 'remove',
      width: 40,
      align: 'center',
      render: (_, consult) => <Tooltip title="Remove this consultation">
        <Button
          type="text"
          size="small"
          icon={<FontAwesomeIcon icon={consult.removed ? faTrashUndo : faTrash} />}
          onClick={e => { e.stopPropagation(); onToggleRemoveConsult(consult) }}
        />
      </Tooltip>,
    },
  ]

  const rows = sortConsultsByDate(consults)

  return <div>
    <Text strong><FontAwesomeIcon icon={faUserClock} /> Scheduled Consultations</Text>
    <Table
      rowKey={(c, index) => (c.id === 0 ? `new-${index}` : String(c.id))}
      columns={columns}
      dataSource={rows}
      pagination={false}
      size="small"
      style={{ marginTop: 8 }}
      onRow={consult => ({
        onClick: () => openEditConsult(consult),
        style: { cursor: 'pointer', opacity: consult.removed ? 0.45 : 1 },
      })}
    />
    <div style={{ marginTop: 12 }}>
      <Button type="primary" icon={<FontAwesomeIcon icon={faUserClock} />} onClick={openNewConsult} disabled={customer.id === 0}>
        Schedule new consultation
      </Button>
    </div>

    <ConsultationModal
      open={consultModalOpen}
      consult={editingConsult}
      customer={customer}
      salesUsers={salesUsersQuery.data ?? []}
      salesLeads={salesLeadsQuery.data ?? []}
      cannedResponses={cannedResponsesQuery.data ?? []}
      projects={projectsQuery.data}
      onClose={() => setConsultModalOpen(false)}
      onSaved={handleConsultSaved}
    />

    <SendConsultationInvitesModal
      open={sendModalOpen}
      customerId={customer.id}
      consult={sendConsult}
      customerEmails={customer.emails ?? []}
      onClose={() => setSendModalOpen(false)}
    />
  </div>
}

export default CustomerConsultsTable
